/**
 * Manejo del JWT en el cliente del admin.
 *
 * Usa una clave de localStorage distinta a la PWA para que dos pestañas
 * (admin + terreno) puedan tener sesiones independientes en el mismo navegador.
 */
import { permisoHabilitado } from "./modulos";

const STORAGE_KEY = "innovasuite.admin.auth";

export type Role = "admin" | "funcionario" | "socio";

export interface AuthState {
  accessToken: string;
  empresaId: number;
  userId: number;
  role: Role;
  isSuperadmin: boolean;
  permisos: string[];
  expiresAt: number;
  /** Nombre del comité del usuario logueado (claim `empresa_nombre`). Lo
   *  emite el login normal para que el topbar muestre el nombre real en
   *  vez de "Empresa #N". */
  empresaNombre?: string;
  /** Si la sesión actual viene de un POST /superadmin/empresas/:id/login-as. */
  viaLoginAs?: boolean;
  /** Nombre del comité visitado, sólo presente cuando viaLoginAs=true. */
  empresaTargetNombre?: string;
  /** Módulos contratados (claim `modulos`). Ausente = plan completo. */
  modulos?: string[];
  /** Comité propio del usuario cuando opera en otro con acceso de oficina
   *  (claim `empresa_origen_id`). Ausente = está en su comité. */
  empresaOrigenId?: number;
}

interface JwtPayload {
  sub: string;
  empresa_id: number;
  role: Role;
  is_superadmin?: boolean;
  permisos?: string[];
  empresa_nombre?: string;
  via_login_as?: boolean;
  empresa_target_nombre?: string;
  modulos?: string[];
  empresa_origen_id?: number;
  exp: number;
  iat: number;
}

export function decodeJwt(token: string): JwtPayload {
  const [, payloadB64] = token.split(".");
  if (!payloadB64) {
    throw new Error("JWT con formato inválido");
  }
  const b64 = payloadB64.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
  return JSON.parse(atob(padded)) as JwtPayload;
}

export function authStateFromToken(accessToken: string): AuthState {
  const claims = decodeJwt(accessToken);
  const state: AuthState = {
    accessToken,
    userId: Number(claims.sub),
    empresaId: claims.empresa_id,
    role: claims.role,
    isSuperadmin: Boolean(claims.is_superadmin),
    permisos: Array.isArray(claims.permisos) ? claims.permisos : [],
    expiresAt: claims.exp * 1000,
  };
  if (typeof claims.empresa_nombre === "string") {
    state.empresaNombre = claims.empresa_nombre;
  }
  if (claims.via_login_as) {
    state.viaLoginAs = true;
  }
  if (typeof claims.empresa_target_nombre === "string") {
    state.empresaTargetNombre = claims.empresa_target_nombre;
  }
  if (Array.isArray(claims.modulos)) {
    state.modulos = claims.modulos;
  }
  if (typeof claims.empresa_origen_id === "number") {
    state.empresaOrigenId = claims.empresa_origen_id;
  }
  return state;
}

/**
 * True si el usuario tiene el permiso pedido. El wildcard `*` y la marca
 * `is_superadmin` conceden acceso, salvo que el permiso sea de un módulo que
 * el comité no contrató.
 */
export function hasPermission(state: AuthState | null, permiso: string): boolean {
  if (!state) return false;
  // El módulo es del comité, no del rol: aplica también al wildcard y al
  // superadmin en login-as.
  if (!permisoHabilitado(permiso, state.modulos)) return false;
  if (state.isSuperadmin) return true;
  if (state.permisos.includes("*")) return true;
  return state.permisos.includes(permiso);
}

export function getAuth(): AuthState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthState> & {
      accessToken: string;
      expiresAt: number;
    };
    if (parsed.expiresAt < Date.now()) {
      clearAuth();
      return null;
    }
    // Sesiones anteriores al campo `permisos`: re-derivamos desde el JWT
    // para no obligar al usuario a re-loguearse.
    if (!Array.isArray(parsed.permisos)) {
      try {
        const migrated = authStateFromToken(parsed.accessToken);
        setAuth(migrated);
        return migrated;
      } catch {
        clearAuth();
        return null;
      }
    }
    return parsed as AuthState;
  } catch {
    return null;
  }
}

export function setAuth(state: AuthState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function isAuthenticated(): boolean {
  return getAuth() !== null;
}
